import React, { useState, useMemo } from "react";
import {
  Search,
  RefreshCw,
  FileSpreadsheet,
  FileText,
  Filter,
  ChevronLeft,
  ChevronRight,
  Package,
  User,
  Calendar,
  Plus,
  Edit2,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
  Layers,
  CheckCircle,
  Square,
  CheckSquare,
  Barcode,
  Upload,
  ClipboardList,
  Disc,
  Flame,
  Sparkles,
  MapPin,
  Boxes
} from "lucide-react";
import { normalizeText, formatMoney } from "../utils/formatters";
import { inventoryService } from "../services/inventoryService";
import { useCatalogContext } from "../contexts/CatalogContext";
import { produtoDadosService } from "../services/produtoDadosService";
import * as XLSX from "xlsx";

// Modais
import EditModal from "../components/modals/EditModal";
import QuickResModal from "../components/modals/QuickResModal";
import EnviarCerasFundicaoModal from "../components/modals/EnviarCerasFundicaoModal";

// Sub-abas de Estoque
import ConferenceTab from "./ConferenceTab";
import SalesTab from "./SalesTab";

// Helper para extrair dinamicamente a medida a partir do SKU
const extractSizeFromSku = (sku) => {
  if (!sku) return null;
  const parts = sku.trim().toUpperCase().split("-");
  if (parts.length < 2) return null;
  
  // Percorre os últimos dois elementos procurando o primeiro número puro
  // e valida se possui no máximo 2 dígitos (uma medida nunca tem 3 dígitos)
  for (let i = parts.length - 1; i >= Math.max(1, parts.length - 2); i--) {
    const part = parts[i];
    if (/^\d+$/.test(part)) {
      if (part.length <= 2) {
        return part;
      }
    }
  }
  return null;
};

export default function StockTab({
  inventory,
  reservations,
  findCatalogItem,
  user,
  loadingCatalog,
  onRefreshCatalog,
  hasAccess,
  
  // PROPS CONFERENCIA (BIPAR)
  barcodeInput,
  setBarcodeInput,
  inputRef,
  handleScanToBuffer,
  scannedBuffer,
  setScannedBuffer,
  isCommitting,
  handleCommitBuffer,
  
  // PROPS BAIXA (SALES)
  salesInput,
  setSalesInput,
  processSales
}) {
  const { isMoldeSku, getSubestoqueCera } = useCatalogContext() || {};

  // --- ESTADOS LOCAIS ---
  const [activeSubTab, setActiveSubTab] = useState("view"); // 'view', 'conference', 'sales'
  const [searchTerm, setSearchTerm] = useState("");
  const [filterModel, setFilterModel] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all");
  const [stockTypeFilter, setStockTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "lastModified",
    direction: "desc",
  });

  // Modais e Seleção
  const [editModal, setEditModal] = useState(null);
  const [quickResModal, setQuickResModal] = useState(null);
  const [fundirModalItem, setFundirModalItem] = useState(null);

  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [zoomedImage, setZoomedImage] = useState(null);

  // Estados da Sub-Aba de Ceras
  const [moldesEstoqueList, setMoldesEstoqueList] = useState([]);
  const [loadingMoldes, setLoadingMoldes] = useState(false);
  const [ceraSearchTerm, setCeraSearchTerm] = useState("");
  const [ceraFilterSaldo, setCeraFilterSaldo] = useState("com_saldo"); // "com_saldo", "todos"
  const [ceraFilterModel, setCeraFilterModel] = useState("all");

  const itemsPerPage = 50;

  // Carrega lista de moldes com estoque de ceras para a sub-aba
  const loadMoldesEstoque = React.useCallback(async () => {
    try {
      setLoadingMoldes(true);
      const list = await produtoDadosService.getAllMoldesWithEstoque();
      setMoldesEstoqueList(list);
    } catch (err) {
      console.warn("Erro ao carregar moldes com estoque:", err);
    } finally {
      setLoadingMoldes(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeSubTab === "ceras") {
      loadMoldesEstoque();
    }
  }, [activeSubTab, loadMoldesEstoque]);

  React.useEffect(() => {
    const handleCeraUpdated = () => {
      if (activeSubTab === "ceras") {
        loadMoldesEstoque();
      }
    };
    window.addEventListener("produto-dados-cera-updated", handleCeraUpdated);
    window.addEventListener("produto-dados-molde-updated", handleCeraUpdated);
    return () => {
      window.removeEventListener("produto-dados-cera-updated", handleCeraUpdated);
      window.removeEventListener("produto-dados-molde-updated", handleCeraUpdated);
    };
  }, [activeSubTab, loadMoldesEstoque]);

  // --- 1. LÓGICA DE AGRUPAMENTO ---
  const groupedInventory = useMemo(() => {
    // Helper de data inline
    const formatDateStr = (val) => {
      if (!val) return null;
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return val; // Se não for data válida, retorna original
        return d.toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch { return val; }
    };

    const groups = {};
    const reservationsMap = {};

    reservations.forEach((r) => {
      if (!reservationsMap[r.sku]) reservationsMap[r.sku] = 0;
      reservationsMap[r.sku] += r.quantity;
    });

    inventory.forEach((i) => {
      const isRealStock = i.status === "in_stock" && i.isPE !== true;
      const isProduction =
        i.isPE === true &&
        i.status !== "adjusted_out" &&
        i.status !== "sold" &&
        i.status !== "pe_interceptado" &&
        i.status !== "in_stock";

      if (!isRealStock && !isProduction) return;

      if (!groups[i.sku]) {
        const d = findCatalogItem ? findCatalogItem(i.sku) : null;
        const sub = getSubestoqueCera ? getSubestoqueCera(i.sku) : null;
        const hasMolde = Boolean(
          (isMoldeSku && isMoldeSku(i.sku)) ||
          d?.tem_molde ||
          d?.full_data?.tem_molde ||
          d?.full_data?.processo_fabricacao === "borracha"
        );
        const ceraBruta = Number(sub?.cera_bruta) || 0;
        const ceraPrecravada = Number(sub?.cera_precravada) || 0;
        const ceraTotal = ceraBruta + ceraPrecravada;
        const gavetaMolde = sub?.localizacao_gaveta || "";

        groups[i.sku] = {
          sku: i.sku,
          baseSku: d?.baseSku || i.sku,
          name: d?.name || "N/I",
          model: d?.model || d?.full_data?.model || d?.full_data?.Model || d?.full_data?.modelo || d?.full_data?.Modelo || "-",
          price: d?.price || 0,
          image: d?.image,

          quantity: 0,
          qtyReal: 0,
          qtyPE: 0,
          peStatuses: {},

          // Dados de Molde e Ceras
          hasMolde,
          ceraBruta,
          ceraPrecravada,
          ceraTotal,
          gavetaMolde,
          estoque_por_aro: sub?.estoque_por_aro || {},

          reservedQuantity: reservationsMap[i.sku] || 0,
          entries: [],
          lastModified: i.timestamp?.seconds || (i.created ? new Date(i.created).getTime() / 1000 : 0),
          lastModifiedStr: formatDateStr(i.dateIn || i.created) || "-",
          lastModifiedUser: i.addedBy,
        };
      }

      groups[i.sku].quantity++;
      if (isRealStock) groups[i.sku].qtyReal++;
      if (isProduction) {
        groups[i.sku].qtyPE++;
        groups[i.sku].peStatuses[i.status] = (groups[i.sku].peStatuses[i.status] || 0) + 1;
      }

      groups[i.sku].entries.push(i);

      // Calcula timestamp atual para comparação
      const currentTs = i.timestamp?.seconds || (i.created ? new Date(i.created).getTime() / 1000 : 0);

      if (currentTs > groups[i.sku].lastModified) {
        groups[i.sku].lastModified = currentTs;
        groups[i.sku].lastModifiedStr = formatDateStr(i.dateIn || i.created) || "-";
        groups[i.sku].lastModifiedUser = i.addedBy;
      }
    });

    Object.values(groups).forEach(
      (g) => (g.displayQuantity = g.qtyReal - g.reservedQuantity)
    );

    return Object.values(groups);
  }, [inventory, reservations, findCatalogItem, isMoldeSku, getSubestoqueCera]);

  // --- 2. FILTROS E BUSCA ---
  const filteredAndSortedGroups = useMemo(() => {
    let result = groupedInventory.filter((group) => {
      const searchLower = normalizeText(searchTerm);
      const skuStr = normalizeText(group.sku);
      const nameStr = normalizeText(group.name);
      const modelStr = normalizeText(group.model);
      
      const matchesSearch = (skuStr.includes(searchLower) ||
          nameStr.includes(searchLower) ||
          modelStr.includes(searchLower));
          
      const matchesModel = filterModel === "all" || String(group.model).toLowerCase() === String(filterModel).toLowerCase();

      // Filtro de Medida (Aro/Tamanho)
      const itemSize = extractSizeFromSku(group.sku);
      const matchesSize = sizeFilter === "all" || itemSize === sizeFilter;

      let matchesStockType = true;
      if (stockTypeFilter === "physical") matchesStockType = group.qtyReal > 0;
      if (stockTypeFilter === "factory") matchesStockType = group.qtyPE > 0;
      if (stockTypeFilter === "has_molde") matchesStockType = group.hasMolde === true;
      if (stockTypeFilter === "has_wax") matchesStockType = group.ceraTotal > 0;

      return matchesSearch && matchesModel && matchesSize && matchesStockType;
    });

    if (sortConfig.key) {
      result.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (typeof valA === "string") valA = valA.toLowerCase();
        if (typeof valB === "string") valB = valB.toLowerCase();
        return (
          (valA < valB ? -1 : 1) * (sortConfig.direction === "asc" ? 1 : -1)
        );
      });
    }
    return result;
  }, [groupedInventory, searchTerm, filterModel, sizeFilter, stockTypeFilter, sortConfig]);

  // --- 3. PAGINAÇÃO ---
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedGroups.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedGroups, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedGroups.length / itemsPerPage);

  const modelsAvailableInSearch = useMemo(() => {
    const models = new Set();
    groupedInventory.forEach((item) => {
      if (item.model && item.model !== "-") models.add(item.model);
    });
    return Array.from(models).sort();
  }, [groupedInventory]);

  // Medidas dinamicamente disponíveis a partir do estoque carregado
  const sizesAvailable = useMemo(() => {
    const sizes = new Set();
    groupedInventory.forEach((item) => {
      const size = extractSizeFromSku(item.sku);
      if (size) sizes.add(size);
    });
    // Ordena numericamente as medidas
    return Array.from(sizes).sort((a, b) => Number(a) - Number(b));
  }, [groupedInventory]);

  // --- 4. AÇÕES ---

  const handleQuickReservation = async (qty, note, customerName) => {
    if (!quickResModal) return;
    try {
      await inventoryService.createReservation(
        quickResModal.sku,
        Number(qty),
        user.name,
        note,
        customerName
      );
      setQuickResModal(null);
      alert("Reserva criada com sucesso!");
    } catch (error) {
      alert("Erro ao criar reserva: " + error.message);
    }
  };

  // --- NOVO: Função para deletar item individual via Modal ---
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Tem certeza que deseja remover este item do estoque?"))
      return;
    try {
      // Busca o item na lista local para saber se é PE
      const item = inventory.find((i) => i.id === itemId);
      const isPE = item ? item.isPE : false; // Fallback false se não achar

      // Chama o serviço atualizado
      await inventoryService.deleteItem(itemId, isPE, user.name);

      alert("Item removido com sucesso!");

      if (onRefreshCatalog) onRefreshCatalog();
    } catch (error) {
      console.error(error);
      alert("Erro ao excluir item: " + error.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSkus.size === 0) return alert("Nada selecionado.");

    let hasConflict = false;
    selectedSkus.forEach((sku) => {
      const group = groupedInventory.find((g) => g.sku === sku);
      if (group && group.reservedQuantity > 0) hasConflict = true;
    });

    if (hasConflict && !window.confirm("Reservas serão afetadas. Continuar?"))
      return;
    if (!window.confirm(`Remover itens de ${selectedSkus.size} SKUs?`)) return;

    try {
      // Coleta todos os IDs e Metadados para chamar o serviço
      const itemsToDelete = []; // { id, isPE }

      selectedSkus.forEach((sku) => {
        const group = groupedInventory.find((g) => g.sku === sku);
        if (!group) return;

        group.entries.forEach((item) => {
          itemsToDelete.push({ id: item.id, isPE: item.isPE });
        });
      });

      // Chama o serviço (BATCH VIA PROMISE ALL NO SERVICE)
      await inventoryService.deleteBatch(itemsToDelete, user.name);

      alert(`${itemsToDelete.length} itens processados.`);
      setSelectedSkus(new Set());

      if (onRefreshCatalog) onRefreshCatalog();
    } catch (e) {
      alert("Erro ao remover: " + e.message);
    }
  };

  // ... (Restante das funções: Export, Sort, etc - mantidas iguais)
  const handleExportXLSX = () => {
    if (groupedInventory.length === 0) return alert("Nenhum dado para exportar.");
    const dataToExport = groupedInventory.map(g => ({
      "SKU": g.sku,
      "Nome": g.name,
      "Modelo": g.model,
      "Estoque Físico": g.qtyReal,
      "Fábrica": g.qtyPE,
      "Tem Molde": g.hasMolde ? "Sim" : "Não",
      "Gaveta Molde": g.gavetaMolde || "-",
      "Ceras Brutas": g.ceraBruta || 0,
      "Ceras Pré-Cravadas": g.ceraPrecravada || 0,
      "Total Ceras": g.ceraTotal || 0,
      "Reservado": g.reservedQuantity,
      "Disponível Lib.": g.displayQuantity,
      "Preço": g.price,
      "Última Modificação": g.lastModifiedStr,
      "Quem Modificou": g.lastModifiedUser
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Estoque Total");
    XLSX.writeFile(workbook, `Estoque_Completo_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExportPDF = () => {
    const printWindow = window.open('', '', 'height=800,width=1000');
    let html = `
      <html>
        <head>
          <title>Relatório de Estoque Semijoias</title>
          <style>
            body { font-family: sans-serif; padding: 20px; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background-color: #f8fafc; font-weight: bold; text-transform: uppercase; color: #475569; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin: 0 0 5px 0; color: #1e293b; }
            .header p { margin: 0; color: #64748b; font-size: 12px; }
            .badge-phys { display: inline-block; padding: 2px 4px; background: #ecfdf5; color: #065f46; border-radius: 4px; font-weight: bold; }
            .badge-pe { display: inline-block; padding: 2px 4px; background: #fff7ed; color: #9a3412; border-radius: 4px; font-weight: bold; }
            .badge-wax { display: inline-block; padding: 2px 4px; background: #fef3c7; color: #78350f; border-radius: 4px; font-weight: bold; }
            .badge-res { display: inline-block; padding: 2px 4px; background: #fef08a; color: #713f12; border-radius: 4px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Relatório de Estoque Filtrado</h2>
            <p>Gerado em: ${new Date().toLocaleString('pt-BR')}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>SKU / Ref</th>
                <th>Nome do Produto</th>
                <th>Modelo</th>
                <th style="text-align:center;">Físico</th>
                <th style="text-align:center;">Fábrica</th>
                <th style="text-align:center;">Ceras</th>
                <th style="text-align:center;">Reservas</th>
                <th style="text-align:center;">Livre</th>
              </tr>
            </thead>
            <tbody>
    `;

    filteredAndSortedGroups.forEach(g => {
      html += `
        <tr>
          <td style="font-weight: bold; color: #2563eb;">${g.sku}</td>
          <td>${g.name}</td>
          <td>${g.model}</td>
          <td style="text-align:center;">${g.qtyReal > 0 ? '<span class="badge-phys">' + g.qtyReal + '</span>' : "0"}</td>
          <td style="text-align:center;">${g.qtyPE > 0 ? '<span class="badge-pe">' + g.qtyPE + '</span>' : "0"}</td>
          <td style="text-align:center;">${g.ceraTotal > 0 ? '<span class="badge-wax">' + g.ceraTotal + (g.gavetaMolde ? ' (' + g.gavetaMolde + ')' : '') + '</span>' : "-"}</td>
          <td style="text-align:center;">${g.reservedQuantity > 0 ? '<span class="badge-res">' + g.reservedQuantity + '</span>' : "0"}</td>
          <td style="text-align:center; font-weight: bold;">${g.displayQuantity}</td>
        </tr>
      `;
    });

    html += `
            </tbody>
          </table>
          <script>
            window.onload = () => { window.print(); window.setTimeout(() => window.close(), 500); }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const requestSort = (key) =>
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  const SortIcon = ({ colKey }) =>
    sortConfig.key !== colKey ? (
      <div className="w-4 h-4" />
    ) : sortConfig.direction === "asc" ? (
      <ChevronUp size={14} />
    ) : (
      <ChevronDown size={14} />
    );
  const toggleSelectOne = (sku) => {
    const newSet = new Set(selectedSkus);
    if (newSet.has(sku)) newSet.delete(sku);
    else newSet.add(sku);
    setSelectedSkus(newSet);
  };
  const toggleSelectAll = () => {
    if (selectedSkus.size === paginatedData.length) setSelectedSkus(new Set());
    else setSelectedSkus(new Set(paginatedData.map((g) => g.sku)));
  };

  // Contadores
  const totalStock = inventory.filter(
    (i) => i.status === "in_stock" && !i.isPE
  ).length;
  const totalPE = inventory.filter(
    (i) =>
      i.isPE === true &&
      i.status !== "adjusted_out" &&
      i.status !== "sold" &&
      i.status !== "pe_interceptado"
  ).length;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden w-full">
      {/* NAVEGAÇÃO DE SUB-ABAS DE ESTOQUE */}
      <div className="flex px-4 pt-4 border-b border-slate-200 bg-white gap-2 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden">
         <button 
            onClick={() => setActiveSubTab("view")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "view" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
         >
            <ClipboardList size={16}/> Visualizar Estoque
         </button>
         <button 
            onClick={() => setActiveSubTab("ceras")}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "ceras" ? "border-amber-600 text-amber-600 font-bold" : "border-transparent text-slate-500 hover:text-slate-700"}`}
         >
            <Flame size={16} className={activeSubTab === "ceras" ? "text-amber-600" : "text-amber-500"} />
            <span>Subestoque de Ceras & Moldes</span>
         </button>
         {hasAccess("conference") && (
           <button 
              onClick={() => setActiveSubTab("conference")}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "conference" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
           >
              <Barcode size={16}/> Modo Leitor (Bipar)
           </button>
         )}
         {hasAccess("sales") && (
           <button 
              onClick={() => setActiveSubTab("sales")}
              className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeSubTab === "sales" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
           >
              <Upload size={16}/> Baixa Manual (Vendas)
           </button>
         )}
      </div>

      {/* SUB-ABA 1: VISUALIZAR ESTOQUE */}
      <div className={`flex-1 flex flex-col overflow-hidden ${activeSubTab === "view" ? "block" : "hidden"}`}>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
          {/* --- MODAIS --- */}
          <EditModal
            isOpen={!!editModal}
            data={editModal}
            onClose={() => setEditModal(null)}
            onDelete={handleDeleteItem}
            user={user}
          />

          {quickResModal && (
            <QuickResModal
              isOpen={!!quickResModal}
              group={quickResModal}
              onClose={() => setQuickResModal(null)}
              onConfirm={handleQuickReservation}
            />
          )}

          {zoomedImage && (
            <div
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fade-in"
              onClick={() => setZoomedImage(null)}
            >
              <div className="relative max-w-4xl max-h-[90vh]">
                <button
                  className="absolute -top-12 right-0 text-white p-2"
                  onClick={() => setZoomedImage(null)}
                >
                  <X size={32} />
                </button>
                <img
                  src={zoomedImage}
                  alt="Zoom"
                  className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                />
              </div>
            </div>
          )}

          <div className="p-4 border-b flex flex-col gap-4 shrink-0">
            <div className="flex gap-4 text-xs font-bold uppercase tracking-wider overflow-x-auto pb-1 no-scrollbar">
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded border border-emerald-100 whitespace-nowrap">
                <CheckCircle size={14} /> Físico: {totalStock}
              </div>
              <div className="flex items-center gap-2 text-orange-600 bg-orange-50 px-3 py-1.5 rounded border border-orange-100 whitespace-nowrap">
                <Layers size={14} /> Fábrica: {totalPE}
              </div>
            </div>

            <div className="flex justify-between gap-4 flex-col lg:flex-row items-start lg:items-center">
              <div className="relative flex-1 w-full">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Pesquisar SKU, Nome ou Modelo..."
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg outline-none focus:border-blue-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                {selectedSkus.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="bg-red-100 text-red-600 px-4 py-2 rounded-lg font-bold text-xs flex items-center gap-2 hover:bg-red-200 grow justify-center"
                  >
                    <Trash2 size={16} /> Excluir ({selectedSkus.size})
                  </button>
                )}
                <button
                  onClick={onRefreshCatalog}
                  disabled={loadingCatalog}
                  className="p-2.5 border rounded-lg hover:bg-slate-50 shrink-0"
                  title="Recarregar Catálogo"
                >
                  <RefreshCw
                    size={20}
                    className={loadingCatalog ? "animate-spin" : ""}
                  />
                </button>
                <div className="flex border rounded-lg overflow-hidden shrink-0">
                  <button
                    onClick={handleExportXLSX}
                    className="px-3 py-2 bg-white hover:bg-green-50 text-green-600 border-r text-xs font-medium flex items-center gap-1"
                  >
                    <FileSpreadsheet size={16} />{" "}
                    <span className="hidden sm:inline">XLSX</span>
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="px-3 py-2 bg-white hover:bg-red-50 text-red-600 text-xs font-medium flex items-center gap-1"
                  >
                    <FileText size={16} />{" "}
                    <span className="hidden sm:inline">PDF</span>
                  </button>
                </div>

                <div className="relative w-full sm:w-48 grow sm:grow-0">
                  <Filter
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={16}
                  />
                  <select
                    className="w-full pl-9 pr-8 py-2.5 border rounded-lg outline-none bg-white text-sm"
                    value={filterModel}
                    onChange={(e) => {
                      setFilterModel(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">Todos Modelos</option>
                    {modelsAvailableInSearch.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative w-full sm:w-48 grow sm:grow-0">
                  <Filter
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={16}
                  />
                  <select
                    className="w-full pl-9 pr-8 py-2.5 border rounded-lg outline-none bg-white text-sm"
                    value={sizeFilter}
                    onChange={(e) => {
                      setSizeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">Todas Medidas</option>
                    {sizesAvailable.map((s) => (
                      <option key={s} value={s}>
                        Medida: {s}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="relative w-full sm:w-48 grow sm:grow-0">
                  <Filter
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={16}
                  />
                  <select
                    className="w-full pl-9 pr-8 py-2.5 border rounded-lg outline-none bg-white text-sm"
                    value={stockTypeFilter}
                    onChange={(e) => {
                      setStockTypeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">Tipo: Todos</option>
                    <option value="physical">Tipo: Somente Físico</option>
                    <option value="factory">Tipo: Somente Fábrica</option>
                    <option value="has_molde">Tipo: Com Molde de Borracha</option>
                    <option value="has_wax">Tipo: Com Ceras Disponíveis</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto custom-scrollbar p-2 md:p-0">
            <table className="w-full text-left border-collapse block md:table">
              <thead className="bg-slate-50 border-b text-xs uppercase text-slate-500 font-bold sticky top-0 z-10 hidden md:table-header-group">
                <tr>
                  <th className="px-4 py-3 w-10 text-center bg-slate-50">
                    <input
                      type="checkbox"
                      checked={
                        selectedSkus.size > 0 &&
                        selectedSkus.size === paginatedData.length
                      }
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 w-16 bg-slate-50">Foto</th>
                  <th
                    className="px-4 py-3 cursor-pointer hover:bg-slate-100 bg-slate-50"
                    onClick={() => requestSort("sku")}
                  >
                    SKU <SortIcon colKey="sku" />
                  </th>
                  <th
                    className="px-4 py-3 cursor-pointer hover:bg-slate-100 bg-slate-50"
                    onClick={() => requestSort("name")}
                  >
                    Nome <SortIcon colKey="name" />
                  </th>
                  <th
                    className="px-4 py-3 cursor-pointer hover:bg-slate-100 bg-slate-50"
                    onClick={() => requestSort("lastModified")}
                  >
                    Última Mod. <SortIcon colKey="lastModified" />
                  </th>
                  <th className="px-4 py-3 bg-slate-50">Quem Modificou</th>
                  <th
                    className="px-4 py-3 text-right cursor-pointer hover:bg-slate-100 bg-slate-50 w-32"
                    onClick={() => requestSort("quantity")}
                  >
                    Disponibilidade <SortIcon colKey="quantity" />
                  </th>
                  <th className="px-4 py-3 text-center bg-slate-50">Ações</th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group space-y-3 md:space-y-0 pb-20">
                {paginatedData.map((group, idx) => (
                  <tr
                    key={idx}
                    className={`block md:table-row relative bg-white border border-slate-200 md:border-b md:border-slate-100 rounded-xl md:rounded-none shadow-sm md:shadow-none hover:bg-blue-50/30 transition-all ${selectedSkus.has(group.sku)
                      ? "bg-blue-50 ring-1 ring-blue-400 md:ring-0"
                      : ""
                      }`}
                  >
                    <td className="block md:table-cell px-4 py-3 text-center absolute top-0 left-0 md:static z-10 p-3 md:p-0">
                      <input
                        type="checkbox"
                        checked={selectedSkus.has(group.sku)}
                        onChange={() => toggleSelectOne(group.sku)}
                        className="w-5 h-5 md:w-4 md:h-4 rounded"
                      />
                    </td>
                    <td className="block md:table-cell px-4 pt-10 pb-2 md:py-3 md:pt-3">
                      <div
                        className="w-16 h-16 md:w-10 md:h-10 rounded bg-slate-100 flex items-center justify-center overflow-hidden cursor-pointer border border-slate-200 md:border-0"
                        onClick={() => group.image && setZoomedImage(group.image)}
                      >
                        {group.image ? (
                          <img
                            src={group.image}
                            className="w-full h-full object-cover"
                            alt=""
                          />
                        ) : (
                          <Package size={20} className="text-slate-300" />
                        )}
                      </div>
                    </td>
                    <td className="block md:table-cell px-4 py-1 md:py-3 absolute top-10 left-20 md:static md:top-auto md:left-auto">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-blue-600 font-bold text-sm md:text-xs">
                          {group.sku}
                        </span>
                        {extractSizeFromSku(group.sku) && (
                          <span className="bg-blue-50 text-blue-700 text-[9px] font-extrabold px-1 py-0.5 rounded border border-blue-200" title="Medida extraída do SKU">
                            Medida: {extractSizeFromSku(group.sku)}
                          </span>
                        )}
                        {group.hasMolde && (
                          <span 
                            className="bg-amber-50 text-amber-900 border border-amber-300 text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 shadow-2xs" 
                            title={group.gavetaMolde ? `Possui molde de borracha na Gaveta: ${group.gavetaMolde}` : "Possui molde de borracha cadastrado na fábrica"}
                          >
                            <Disc size={10} className="text-amber-700" />
                            <span>Molde{group.gavetaMolde ? ` (${group.gavetaMolde})` : ""}</span>
                          </span>
                        )}
                        {group.reservedQuantity > 0 && (
                          <span
                            className="bg-yellow-400 text-yellow-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                            title="Reservados"
                          >
                            R {group.reservedQuantity}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="block md:table-cell px-4 py-1 md:py-3 md:pl-0 pl-[5.5rem] -mt-8 md:mt-0 mb-4 md:mb-0">
                      <div className="text-slate-700 text-xs font-medium line-clamp-2 md:line-clamp-1">
                        {group.name}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1 rounded">
                          {formatMoney(group.price)}
                        </span>
                        <span className="text-[10px] text-slate-400 truncate max-w-[100px]">
                          {group.model}
                        </span>
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={12} /> {group.lastModifiedStr || "-"}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-xs text-slate-500">
                      <div className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded w-fit">
                        <User size={10} /> {group.lastModifiedUser || "-"}
                      </div>
                    </td>
                    <td className="block md:table-cell px-4 py-3 md:text-right absolute top-0 right-0 md:static p-3 md:p-0">
                      <div className="flex flex-row md:flex-col items-center md:items-end gap-2 md:gap-1">
                        {group.qtyReal > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            {group.qtyReal}{" "}
                            <span className="text-[9px] uppercase font-normal text-emerald-600">
                              Disp.
                            </span>
                          </span>
                        ) : null}
                        {group.qtyPE > 0 && (
                          <div className="flex flex-col items-end gap-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200 w-fit">
                              <Layers size={10} /> {group.qtyPE}{" "}
                              <span className="text-[9px] uppercase font-normal text-orange-600">
                                Prod.
                              </span>
                            </span>
                            {group.peStatuses && Object.keys(group.peStatuses).length > 0 && (
                              <div className="text-[9px] text-slate-500 font-medium leading-tight text-right w-full">
                                {Object.entries(group.peStatuses).map(([status, q]) => (
                                  <div key={status}>{q}x {status.toUpperCase()}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {group.ceraTotal > 0 ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <button
                              type="button"
                              onClick={() => setFundirModalItem({
                                sku: group.sku,
                                name: group.name,
                                image: group.image,
                                gaveta: group.gavetaMolde,
                                cera_bruta: group.ceraBruta,
                                cera_precravada: group.ceraPrecravada,
                                estoque_por_aro: group.estoque_por_aro || {},
                                tipo_modelo: group.model || "",
                                initialMode: "fundir"
                              })}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold bg-amber-100/90 hover:bg-amber-200 text-amber-900 border border-amber-300 w-fit cursor-pointer transition active:scale-95 shadow-2xs"
                              title={`Ceras na gaveta: ${group.ceraBruta} brutas + ${group.ceraPrecravada} pré-cravadas${group.gavetaMolde ? ` (Gaveta ${group.gavetaMolde})` : ""}. Clique para Mandar Fundir ou Injetar!`}
                            >
                              <Flame size={10} className="text-amber-700 animate-pulse" /> {group.ceraTotal}{" "}
                              <span className="text-[9px] uppercase font-bold text-amber-700">
                                Ceras ⚡
                              </span>
                            </button>
                            {(group.ceraBruta > 0 && group.ceraPrecravada > 0) && (
                              <div className="text-[9px] text-amber-800/80 font-medium leading-none text-right">
                                {group.ceraBruta}b / {group.ceraPrecravada}c
                              </div>
                            )}
                          </div>
                        ) : group.hasMolde ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <button
                              type="button"
                              onClick={() => setFundirModalItem({
                                sku: group.sku,
                                name: group.name,
                                image: group.image,
                                gaveta: group.gavetaMolde,
                                cera_bruta: 0,
                                cera_precravada: 0,
                                estoque_por_aro: group.estoque_por_aro || {},
                                tipo_modelo: group.model || ""
                              })}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 w-fit cursor-pointer transition active:scale-95 shadow-2xs"
                              title="Molde disponível. Clique para solicitar produção de estoque!"
                            >
                              <Disc size={10} className="text-amber-600" />
                              <span>+ Produzir</span>
                            </button>
                          </div>
                        ) : null}
                        {group.qtyReal === 0 && group.qtyPE === 0 && group.ceraTotal === 0 && !group.hasMolde && (
                          <span className="text-slate-300 text-xs font-bold">
                            0
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="block md:table-cell px-4 py-2 md:py-3 text-center border-t md:border-0 bg-slate-50 md:bg-transparent rounded-b-xl md:rounded-none">
                      <div className="flex gap-1.5 justify-center flex-wrap">
                        {group.hasMolde && (
                          <button
                            type="button"
                            onClick={() => setFundirModalItem({
                              sku: group.sku,
                              name: group.name,
                              image: group.image,
                              gaveta: group.gavetaMolde,
                              cera_bruta: group.ceraBruta,
                              cera_precravada: group.ceraPrecravada,
                              estoque_por_aro: group.estoque_por_aro || {},
                              tipo_modelo: group.model || ""
                            })}
                            className="flex-1 md:flex-none p-1.5 text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-300 rounded transition-colors shadow-2xs flex items-center justify-center gap-1 text-xs font-bold cursor-pointer"
                            title="Solicitar produção de estoque para este molde"
                          >
                            <Flame size={15} className="text-amber-600" />{" "}
                            <span className="md:hidden">Produzir</span>
                          </button>
                        )}
                        <button
                          onClick={() => setQuickResModal(group)}
                          className="flex-1 md:flex-none p-1.5 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors shadow-sm flex items-center justify-center gap-1 text-xs font-bold cursor-pointer"
                          title="Reserva Rápida"
                        >
                          <Plus size={16} />{" "}
                          <span className="md:hidden">Reservar</span>
                        </button>
                        <button
                          onClick={() => setEditModal(group)}
                          className="flex-1 md:flex-none p-1.5 text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50 hover:text-blue-600 transition-colors flex items-center justify-center gap-1 text-xs font-bold cursor-pointer"
                          title="Editar Estoque"
                        >
                          <Edit2 size={16} />{" "}
                          <span className="md:hidden">Editar</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {paginatedData.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-6 py-8 text-center text-slate-400 block md:table-cell"
                    >
                      Nenhum item encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 flex justify-between items-center bg-slate-50 text-xs text-slate-500 border-t shrink-0">
            <span>
              {(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, filteredAndSortedGroups.length)}{" "}
              de {filteredAndSortedGroups.length}
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="p-1.5 rounded bg-white border hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="p-1.5 rounded bg-white border hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SUB-ABA: SUBESTOQUE DE CERAS & MOLDES */}
      {activeSubTab === "ceras" && (
        <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6 bg-slate-50 gap-4">
          {/* CARDS DE KPIS */}
          {(() => {
            const totalMoldes = moldesEstoqueList.length;
            let totalBrutas = 0;
            let totalPrecravadas = 0;
            let moldesComSaldo = 0;

            moldesEstoqueList.forEach((m) => {
              const b = Number(m.subestoque?.cera_bruta || 0);
              const p = Number(m.subestoque?.cera_precravada || 0);
              totalBrutas += b;
              totalPrecravadas += p;
              if (b + p > 0) moldesComSaldo++;
            });

            const totalGeral = totalBrutas + totalPrecravadas;

            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0">
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
                  <div className="flex items-center justify-between text-slate-400">
                    <span className="text-[10px] font-bold uppercase tracking-wider">Moldes Ativos</span>
                    <Disc size={15} className="text-indigo-600" />
                  </div>
                  <div className="text-xl font-black text-slate-800 mt-1">
                    {totalMoldes} <span className="text-xs font-semibold text-slate-400">modelos</span>
                  </div>
                  <div className="text-[11px] text-emerald-600 font-medium mt-0.5">
                    {moldesComSaldo} com ceras prontas
                  </div>
                </div>

                <div className="bg-white p-3.5 rounded-xl border border-amber-200 shadow-2xs bg-gradient-to-br from-white to-amber-50/40">
                  <div className="flex items-center justify-between text-amber-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider">Ceras Brutas</span>
                    <Flame size={15} className="text-amber-600" />
                  </div>
                  <div className="text-xl font-black text-amber-900 mt-1">
                    {totalBrutas} <span className="text-xs font-semibold text-amber-700">un</span>
                  </div>
                  <div className="text-[11px] text-amber-700/80 font-medium mt-0.5">
                    Prontas para fundição direta
                  </div>
                </div>

                <div className="bg-white p-3.5 rounded-xl border border-purple-200 shadow-2xs bg-gradient-to-br from-white to-purple-50/40">
                  <div className="flex items-center justify-between text-purple-700">
                    <span className="text-[10px] font-bold uppercase tracking-wider">Pré-Cravadas</span>
                    <Sparkles size={15} className="text-purple-600" />
                  </div>
                  <div className="text-xl font-black text-purple-900 mt-1">
                    {totalPrecravadas} <span className="text-xs font-semibold text-purple-700">un</span>
                  </div>
                  <div className="text-[11px] text-purple-700/80 font-medium mt-0.5">
                    Cravadas em cera na bancada
                  </div>
                </div>

                <div className="bg-slate-900 p-3.5 rounded-xl border border-slate-800 shadow-md text-white">
                  <div className="flex items-center justify-between text-slate-400">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Total de Ceras</span>
                    <Boxes size={15} className="text-amber-400" />
                  </div>
                  <div className="text-xl font-black text-white mt-1">
                    {totalGeral} <span className="text-xs font-semibold text-slate-400">un na gaveta</span>
                  </div>
                  <div className="text-[11px] text-amber-300 font-medium mt-0.5">
                    Capacidade de reposição rápida
                  </div>
                </div>
              </div>
            );
          })()}

          {/* PAINEL DA TABELA DE CERAS */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col flex-1">
            {/* BARRA DE FILTROS */}
            <div className="p-3.5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-slate-50/60">
              <div className="flex items-center gap-3 flex-1 min-w-[280px]">
                <div className="relative flex-1 max-w-md">
                  <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar por SKU, nome da joia ou gaveta..."
                    value={ceraSearchTerm}
                    onChange={(e) => setCeraSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium focus:ring-2 focus:ring-amber-500 focus:outline-hidden"
                  />
                  {ceraSearchTerm && (
                    <button
                      onClick={() => setCeraSearchTerm("")}
                      className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>

                <div className="flex items-center bg-white p-1 rounded-lg border border-slate-200 text-xs">
                  <button
                    type="button"
                    onClick={() => setCeraFilterSaldo("com_saldo")}
                    className={`px-2.5 py-1 rounded font-bold transition ${
                      ceraFilterSaldo === "com_saldo"
                        ? "bg-amber-600 text-white shadow-2xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Com Ceras (&gt; 0)
                  </button>
                  <button
                    type="button"
                    onClick={() => setCeraFilterSaldo("todos")}
                    className={`px-2.5 py-1 rounded font-bold transition ${
                      ceraFilterSaldo === "todos"
                        ? "bg-slate-800 text-white shadow-2xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Todos os Moldes
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={loadMoldesEstoque}
                  disabled={loadingMoldes}
                  className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-300 rounded-lg flex items-center gap-1.5 transition shadow-2xs cursor-pointer"
                  title="Atualizar dados de ceras"
                >
                  <RefreshCw size={13} className={loadingMoldes ? "animate-spin text-amber-600" : ""} />
                  <span>Atualizar</span>
                </button>
              </div>
            </div>

            {/* TABELA DE MOLDES E CERAS */}
            <div className="flex-1 overflow-auto">
              {(() => {
                const term = ceraSearchTerm.trim().toLowerCase();
                const filtered = moldesEstoqueList.filter((m) => {
                  const est = m.subestoque || {};
                  const bruta = Number(est.cera_bruta || 0);
                  const precravada = Number(est.cera_precravada || 0);
                  const total = bruta + precravada;

                  if (ceraFilterSaldo === "com_saldo" && total <= 0) return false;

                  if (term) {
                    const cat = findCatalogItem ? findCatalogItem(m.skuPai) : null;
                    const name = (cat?.name || m.ficha_tecnica?.nome || "").toLowerCase();
                    const gaveta = (est.localizacao_gaveta || "").toLowerCase();
                    const sku = m.skuPai.toLowerCase();
                    const codigo = (m.config_borracha?.codigo_maquina || "").toLowerCase();
                    if (!sku.includes(term) && !name.includes(term) && !gaveta.includes(term) && !codigo.includes(term)) {
                      return false;
                    }
                  }

                  return true;
                });

                if (loadingMoldes && filtered.length === 0) {
                  return (
                    <div className="p-12 text-center text-xs text-slate-400 flex flex-col items-center justify-center gap-2">
                      <RefreshCw size={20} className="animate-spin text-amber-600" />
                      <span>Consultando gavetas e saldos de ceras da fábrica...</span>
                    </div>
                  );
                }

                if (filtered.length === 0) {
                  return (
                    <div className="p-12 text-center text-xs text-slate-400">
                      Nenhum molde com os filtros selecionados.
                    </div>
                  );
                }

                return (
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-slate-50 text-slate-600 uppercase font-bold text-[10px] tracking-wider border-b border-slate-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3">Joia / SKU</th>
                        <th className="px-4 py-3">Gaveta</th>
                        <th className="px-4 py-3 text-center">Cera Bruta</th>
                        <th className="px-4 py-3 text-center">Pré-Cravada</th>
                        <th className="px-4 py-3 text-center">Total Ceras</th>
                        <th className="px-4 py-3 text-center">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filtered.map((m) => {
                        const cat = findCatalogItem ? findCatalogItem(m.skuPai) : null;
                        const name = cat?.name || m.ficha_tecnica?.nome || m.skuPai;
                        const image = cat?.image || cat?.image_url || m.arquivos_imagens?.[0] || "";
                        const est = m.subestoque || {};
                        const bruta = Number(est.cera_bruta || 0);
                        const precravada = Number(est.cera_precravada || 0);
                        const total = bruta + precravada;
                        const gaveta = est.localizacao_gaveta || "N/I";

                        return (
                          <tr key={m.skuPai} className="hover:bg-amber-50/40 transition">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-3">
                                {image ? (
                                  <img
                                    src={image}
                                    alt={name}
                                    className="w-10 h-10 object-cover rounded-lg border border-slate-200 bg-white shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                                    <Package size={16} />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <div className="font-mono font-bold text-xs text-indigo-700">
                                    {m.skuPai}
                                  </div>
                                  <div className="text-xs font-semibold text-slate-800 truncate max-w-xs" title={name}>
                                    {name}
                                  </div>
                                  {m.subestoque?.estoque_por_aro && Object.keys(m.subestoque.estoque_por_aro).length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap mt-1">
                                      {Object.entries(m.subestoque.estoque_por_aro)
                                        .filter(([_, q]) => (q.bruta > 0 || q.precravada > 0))
                                        .sort((a, b) => (parseInt(a[0], 10) || 0) - (parseInt(b[0], 10) || 0))
                                        .map(([aro, q]) => (
                                          <span
                                            key={aro}
                                            className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-amber-50 text-amber-950 border border-amber-200"
                                            title={`Aro ${aro}: ${q.bruta} brutas, ${q.precravada} cravadas`}
                                          >
                                            {aro}: <span className="text-amber-800">{q.bruta}b</span>{q.precravada > 0 && <span className="text-purple-700">+{q.precravada}c</span>}
                                          </span>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="px-4 py-2.5">
                              <span className="inline-flex items-center gap-1 font-bold text-[11px] text-amber-900 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md shadow-2xs">
                                <MapPin size={11} className="text-amber-700" />
                                {gaveta}
                              </span>
                            </td>

                            <td className="px-4 py-2.5 text-center font-bold text-slate-700">
                              {bruta > 0 ? (
                                <span className="inline-block px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-xs font-black">
                                  {bruta} un
                                </span>
                              ) : (
                                <span className="text-slate-300 font-normal">0</span>
                              )}
                            </td>

                            <td className="px-4 py-2.5 text-center font-bold text-slate-700">
                              {precravada > 0 ? (
                                <span className="inline-block px-2 py-0.5 rounded-md bg-purple-100 text-purple-900 text-xs font-black">
                                  {precravada} un
                                </span>
                              ) : (
                                <span className="text-slate-300 font-normal">0</span>
                              )}
                            </td>

                            <td className="px-4 py-2.5 text-center">
                              {total > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-black text-xs shadow-xs">
                                  <Flame size={12} className="text-amber-200" />
                                  {total} un
                                </span>
                              ) : (
                                <span className="text-slate-300 text-xs font-bold">0</span>
                              )}
                            </td>

                            <td className="px-4 py-2.5 text-center">
                              <button
                                type="button"
                                onClick={() => setFundirModalItem({
                                  sku: m.skuPai,
                                  name,
                                  image,
                                  gaveta,
                                  cera_bruta: bruta,
                                  cera_precravada: precravada,
                                  estoque_por_aro: m.subestoque?.estoque_por_aro || {},
                                  config_borracha: m.config_borracha || {},
                                  tipo_modelo: m.config_borracha?.tipo_modelo || m.ficha_tecnica?.tipo_modelo || ""
                                })}
                                className="px-3.5 py-1.5 rounded-xl font-bold text-xs transition shadow-2xs flex items-center gap-1.5 mx-auto cursor-pointer active:scale-95 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300"
                                title="Solicitar produção de estoque para este molde"
                              >
                                <Flame size={13} className="text-amber-600" />
                                <span>⚡ Pedir Estoque</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* SUB-ABA 2: MODO LEITOR (CONFERENCIA) */}
      {activeSubTab === "conference" && hasAccess("conference") && (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50">
          <ConferenceTab
            barcodeInput={barcodeInput}
            setBarcodeInput={setBarcodeInput}
            inputRef={inputRef}
            handleScanToBuffer={handleScanToBuffer}
            scannedBuffer={scannedBuffer}
            setScannedBuffer={setScannedBuffer}
            isCommitting={isCommitting}
            handleCommitBuffer={handleCommitBuffer}
            handleClearBuffer={() => setScannedBuffer([])}
            removeItemFromBuffer={(id) =>
              setScannedBuffer((p) => p.filter((i) => i.tempId !== id))
            }
            bufferPage={1}
            setBufferPage={() => {}}
            paginatedBuffer={scannedBuffer}
            totalBufferPages={1}
          />
        </div>
      )}

      {/* SUB-ABA 3: BAIXA MANUAL (VENDAS) */}
      {activeSubTab === "sales" && hasAccess("sales") && (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50">
          <SalesTab
            salesInput={salesInput}
            setSalesInput={setSalesInput}
            processSales={processSales}
          />
        </div>
      )}

      {/* MODAL GLOBAL: ENVIAR CERAS PARA FUNDIÇÃO */}
      {fundirModalItem && (
        <EnviarCerasFundicaoModal
          isOpen={!!fundirModalItem}
          onClose={() => setFundirModalItem(null)}
          item={fundirModalItem}
          user={user}
          onSuccess={() => {
            loadMoldesEstoque();
          }}
        />
      )}
    </div>
  );
}
